import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { IconMinus, IconTrash } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'

import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { formatVersionDate } from '../drawerHelpers'
import { useOverrideEditing } from '../hooks/useOverrideEditing'
import VersionViewingStrip from './VersionViewingStrip'

// =============================================================================
// OverrideEditor — single-field description card with edit + version history.
// Used by VariablesTab and ToolsTab; the caller supplies the avatar glyph and
// any header badge (e.g. the "required" pill) so the card chrome stays shared.
//
// States:
//   read     — the value sits in a borderless box that fills gray on hover;
//              click it to edit
//   editing  — textarea with ⌘↵ to save / Esc to cancel + Cancel/Save
//   history  — "All edits (N)" expands a read-only list (green LATEST pill);
//              clicking an older row peeks it (viewing strip + Revert), and a
//              Reset-to-default action sits below the list when an override
//              exists.
// =============================================================================

// Newest-first version list. Click a row to peek it; the latest shows the green
// LATEST pill, others reserve its width (hidden) so the column stays aligned.
// Exported for LlmSettingsPanel, which shares the card chrome + history UX but
// swaps the textarea body for capability-driven controls.
export const OverrideHistoryList = ({
  testIdPrefix,
  ordered,
  latestVersionNumber,
  peekVersion,
  onPeek,
}) => (
  <div className="voice-agents__version-list" data-testid={`${testIdPrefix}-history`}>
    {ordered.map((entry) => {
      const isLatest = entry.version === latestVersionNumber
      const isPeeking = peekVersion === entry.version
      return (
        <button
          type="button"
          key={entry.version}
          className={classNames('voice-agents__version-row', {
            'voice-agents__version-row--latest': isLatest,
            'voice-agents__version-row--viewing': isPeeking && !isLatest,
          })}
          onClick={() => onPeek(entry.version)}
          data-testid={`${testIdPrefix}-history-peek-${entry.version}`}
          data-version={entry.version}
        >
          <div className="voice-agents__version-row-date">{formatVersionDate(entry.createdAt)}</div>
          <div className="voice-agents__version-row-body">
            <div className="voice-agents__version-row-author">by {entry.createdBy || '—'}</div>
            <div className="voice-agents__version-row-summary">
              {entry.content || entry.summary || '(no content)'}
            </div>
          </div>
          <div
            className={classNames('voice-agents__version-row-badges', {
              'voice-agents__version-row-badges--hidden': !isLatest,
            })}
          >
            <Pill success xs data-testid={`${testIdPrefix}-history-badge-latest-${entry.version}`}>
              LATEST
            </Pill>
          </div>
        </button>
      )
    })}
  </div>
)

const OverrideEditor = ({
  testIdPrefix,
  icon,
  label,
  displayName,
  nameBadge,
  description,
  value,
  emptyHint,
  editorRows = 4,
  editorPlaceholder = '',
  versions = [],
  hasOverride = false,
  isSaving = false,
  isReverting = false,
  isResetting = false,
  onSave,
  onRevert,
  onReset,
}) => {
  const [historyOpen, setHistoryOpen] = useState(false)
  // The version the user is peeking. When set, the body shows that version's
  // content read-only and a viewing strip offers a single Revert affordance.
  const [peekVersion, setPeekVersion] = useState(null)

  const {
    editing,
    draft,
    setDraft,
    revertTarget,
    setRevertTarget,
    resetConfirmOpen,
    setResetConfirmOpen,
    startEdit,
    cancelEdit,
    save,
    confirmRevert,
    confirmReset,
  } = useOverrideEditing({ onSave, onRevert, onReset, onAfterMutate: () => setPeekVersion(null) })

  const ordered = [...versions].sort((a, b) => (b.version || 0) - (a.version || 0))
  const latestEntry = ordered[0]
  const latestVersionNumber = latestEntry?.version
  const peekEntry =
    peekVersion !== null ? ordered.find((entry) => entry.version === peekVersion) : null
  const isViewing = Boolean(peekEntry) && peekEntry.version !== latestVersionNumber

  // Drop the peek if the version disappeared (e.g. after a reset/refetch).
  useEffect(() => {
    if (peekVersion !== null && !peekEntry) {
      setPeekVersion(null)
    }
  }, [peekVersion, peekEntry])

  const handlePeek = (versionNumber) => {
    if (versionNumber === latestVersionNumber) {
      setPeekVersion(null)
      return
    }
    setPeekVersion(peekVersion === versionNumber ? null : versionNumber)
  }

  const handleToggleHistory = () => {
    // Leaving history view drops any peek so the body returns to the live value.
    if (historyOpen) {
      setPeekVersion(null)
    }
    setHistoryOpen(!historyOpen)
  }

  const handleEditKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Stop the bubble so Esc cancels the inline edit without also closing the
      // surrounding drawer/modal.
      event.preventDefault()
      event.stopPropagation()
      cancelEdit()
    } else if (
      (event.metaKey || event.ctrlKey) &&
      event.key === 'Enter' &&
      !isSaving &&
      !isEmpty(draft.trim())
    ) {
      // Prevent the keystroke from inserting a newline in the textarea, and
      // gate on isSaving so a rapid double-press can't double-submit.
      event.preventDefault()
      save()
    }
  }

  const versionCount = ordered.length
  const displayValue = isViewing ? peekEntry.content || peekEntry.summary : value
  const showEmpty = !displayValue
  // The hover-gray box is the click-to-edit target — only in the plain read
  // state (not editing, not peeking an older version, not browsing history).
  const canInlineEdit = !editing && !isViewing && !historyOpen
  const showResetRow = historyOpen && hasOverride && !editing && !isViewing

  return (
    <div className="voice-agents__override-editor" data-testid={`${testIdPrefix}-editor`}>
      <div className="voice-agents__override-editor-head">
        <div className="voice-agents__override-editor-title">
          {icon && <span className="voice-agents__override-editor-avatar">{icon}</span>}
          <span className="voice-agents__override-editor-label">{label}</span>
          {nameBadge}
        </div>
        {versionCount > 0 ? (
          <button
            type="button"
            className="voice-agents__override-editor-edits-toggle"
            onClick={handleToggleHistory}
            data-testid={`${testIdPrefix}-history-toggle`}
          >
            {historyOpen ? (
              <>
                <IconMinus size={14} /> Hide edits
              </>
            ) : (
              `All edits (${versionCount})`
            )}
          </button>
        ) : (
          <span className="voice-agents__override-editor-edits-toggle voice-agents__override-editor-edits-toggle--empty">
            All edits (0)
          </span>
        )}
      </div>

      {description && (
        <div
          className="voice-agents__override-editor-readonly-desc"
          data-testid={`${testIdPrefix}-description`}
        >
          {description}
        </div>
      )}

      {editing ? (
        <div className="voice-agents__override-editor-editbox" data-testid={`${testIdPrefix}-edit`}>
          <textarea
            className="voice-agents__override-editor-textarea"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleEditKeyDown}
            rows={editorRows}
            placeholder={editorPlaceholder}
            data-testid={`${testIdPrefix}-textarea`}
            aria-label={`Edit ${displayName}`}
            autoFocus
          />
          <div className="voice-agents__override-editor-edit-footer">
            <span className="voice-agents__override-editor-kbd-hint">
              ⌘↵ to save · Esc to cancel
            </span>
            <div className="voice-agents__override-editor-edit-actions">
              <Button small onClick={cancelEdit} data-testid={`${testIdPrefix}-cancel`}>
                Cancel
              </Button>
              <Button
                brand
                small
                onClick={save}
                disabled={isSaving || isEmpty(draft.trim())}
                data-testid={`${testIdPrefix}-save`}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      ) : canInlineEdit ? (
        <button
          type="button"
          className={classNames('voice-agents__override-editor-descbox', {
            'voice-agents__override-editor-descbox--empty': showEmpty,
          })}
          onClick={() => startEdit(value)}
          disabled={isReverting || isResetting}
          data-testid={`${testIdPrefix}-value`}
          title="Click to edit"
        >
          {displayValue || emptyHint || '—'}
        </button>
      ) : (
        <div
          className={classNames('voice-agents__override-editor-plain', {
            'voice-agents__override-editor-plain--empty': showEmpty,
          })}
          data-testid={`${testIdPrefix}-value`}
        >
          {displayValue || emptyHint || '—'}
        </div>
      )}

      {isViewing && (
        <VersionViewingStrip
          testIdPrefix={testIdPrefix}
          version={peekEntry}
          busy={isReverting}
          onRevert={() => setRevertTarget(peekEntry)}
        />
      )}

      {historyOpen && versionCount > 0 && (
        <OverrideHistoryList
          testIdPrefix={testIdPrefix}
          ordered={ordered}
          latestVersionNumber={latestVersionNumber}
          peekVersion={peekVersion}
          onPeek={handlePeek}
        />
      )}

      {showResetRow && (
        <div className="voice-agents__override-editor-reset-row">
          <Button
            basic
            small
            critical
            onClick={() => setResetConfirmOpen(true)}
            disabled={isResetting}
            data-testid={`${testIdPrefix}-reset`}
            title="Drop every override and fall back to the engineer default"
          >
            <IconTrash /> Reset to default
          </Button>
        </div>
      )}

      <ConfirmModal
        show={Boolean(revertTarget)}
        onClose={() => setRevertTarget(null)}
        title={
          revertTarget
            ? `Revert ${displayName} to ${formatVersionDate(revertTarget.createdAt)}?`
            : 'Revert'
        }
        confirmLabel="Revert"
        busyLabel="Reverting…"
        busy={isReverting}
        confirmDisabled={!revertTarget}
        onConfirm={confirmRevert}
        testIdPrefix={`${testIdPrefix}-revert`}
      >
        {revertTarget && (
          <p>
            This creates a <strong>new edit</strong> with the content from{' '}
            <strong>{formatVersionDate(revertTarget.createdAt)}</strong>. The current value stays in
            history.
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        show={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        title={`Reset ${displayName} to default?`}
        confirmLabel="Reset"
        busyLabel="Resetting…"
        busy={isResetting}
        destructive
        onConfirm={confirmReset}
        testIdPrefix={`${testIdPrefix}-reset`}
      >
        <p>
          Drops every override on this field and falls back to the engineer default. The current
          value will be discarded.
        </p>
      </ConfirmModal>
    </div>
  )
}

export default OverrideEditor
